import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { NumberInput } from '@/modules/common/components/inputs/';
import { NumUtils } from '@/modules/common/utils/NumUtils';

import { MAX_LIST_HEIGHT } from './constants';
import { AddRow } from './AddRow';
import { OptionRow } from './OptionRow';
import { pruneRecord, removeKey } from './utils';
import { OptionsEditorPopoverShell } from './OptionsEditorPopoverShell';
import type { NumberOptionsEditorPopoverProps } from './types';

function NumberOptionsEditorPopoverImpl({
  disabled,
  ariaLabel,
  icon,
  title,
  width,
  maxWidth,
  anchorOrigin,
  transformOrigin,
  onInputFocus,
  onInputBlur,
  options,
  unit,
  onUpdateOption,
  onDeleteOption,
  onAddOption,
  addPlaceholder,
  integerOnly = false,
}: NumberOptionsEditorPopoverProps) {
  const { t } = useTranslation('interface');

  const [newText, setNewText] = useState('');
  const [drafts, setDrafts] = useState<Record<string, number>>({});

  // Keep a ref so commit handlers don't need to depend on drafts.
  const draftsRef = useRef(drafts);
  useEffect(() => {
    draftsRef.current = drafts;
  }, [drafts]);

  const reset = useCallback(() => {
    setNewText('');
    setDrafts({});
  }, []);

  // Whenever the list of options changes, delete any local draft edits that belong to options that no longer exist.
  useEffect(() => {
    const keys = new Set(options.map((x) => String(x)));
    // plz fix me
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDrafts((prev) => pruneRecord(prev, keys));
  }, [options]);

  const duplicateCounts = useMemo(() => {
    const counts = new Map<string, number>();
    options.forEach((value) => {
      const key = String(drafts[String(value)] ?? value);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return counts;
  }, [drafts, options]);

  const parsedNewNumber = useMemo(() => NumUtils.parseNonNegativeNumber(newText), [newText]);
  
  const newTextIsInvalid = useMemo(
    () => newText.trim().length > 0 && parsedNewNumber === null,
    [newText, parsedNewNumber],
  );

  const newTextIsDuplicate = useMemo(() => {
    if (parsedNewNumber === null) return false;
    return (duplicateCounts.get(String(parsedNewNumber)) ?? 0) > 0;
  }, [duplicateCounts, parsedNewNumber]);

  const canAdd = useMemo(() => {
    if (disabled) return false;
    return parsedNewNumber !== null && !newTextIsDuplicate && !newTextIsInvalid;
  }, [disabled, newTextIsDuplicate, newTextIsInvalid, parsedNewNumber]);

  const resolveErrorText = useCallback(
    (flags: { invalid?: boolean; duplicate?: boolean }) => {
      if (flags.invalid) {
        return t('select_options_editor.invalid_value', 'Invalid value');
      }
      if (flags.duplicate) {
        return t('select_options_editor.duplicate', 'Duplicate');
      }
      return undefined;
    },
    [t],
  );

  const newTextInvalidErrorText = useMemo(
    () => resolveErrorText({ invalid: newTextIsInvalid, duplicate: newTextIsDuplicate }),
    [resolveErrorText, newTextIsInvalid, newTextIsDuplicate],
  );

  const commitDraft = useCallback(
    async (oldValue: number, explicitNextValue?: number) => {
      const key = String(oldValue);
      const nextValue = explicitNextValue ?? draftsRef.current[key];
      if (nextValue === undefined) return;

      // If user ended up with the same value, just clear draft.
      if (nextValue === oldValue) {
        setDrafts((prev) => removeKey(prev, key));
        return;
      }

      if (!Number.isFinite(nextValue) || nextValue < 0) return;
      if (integerOnly && !Number.isInteger(nextValue)) return;
      if ((duplicateCounts.get(String(nextValue)) ?? 0) > 1) return;

      await onUpdateOption(oldValue, nextValue);
      setDrafts((prev) => removeKey(prev, key));
    },
    [duplicateCounts, integerOnly, onUpdateOption],
  );

  const addOption = useCallback(async () => {
    if (!canAdd) return;
    const value = parsedNewNumber;
    if (value === null) return;

    await onAddOption(value);
    setNewText('');
  }, [canAdd, onAddOption, parsedNewNumber]);

  const handleBlur = useCallback(
    (oldValue: number) => async (nextValue?: number) => {
      try {
        await commitDraft(oldValue, nextValue);
      } finally {
        onInputBlur?.();
      }
    },
    [commitDraft, onInputBlur],
  );

  const handleRemove = useCallback(
    (value: number) => () => {
      const key = String(value);
      setDrafts((prev) => removeKey(prev, key));
      onDeleteOption(value);
    },
    [onDeleteOption],
  );

  return (
    <OptionsEditorPopoverShell
      disabled={disabled}
      ariaLabel={ariaLabel}
      icon={icon}
      title={title}
      width={width}
      maxWidth={maxWidth}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      onClose={reset}
    >
      <Stack gap={1} sx={{ width: '100%' }}>
        <Stack
          gap={1}
          sx={{ width: '100%', maxHeight: MAX_LIST_HEIGHT, overflowY: 'auto', pr: 0.5 }}
        >
          {options.map((value) => {
            const key = String(value);
            const effective = drafts[key] ?? value;

            const duplicate = (duplicateCounts.get(String(effective)) ?? 0) > 1;
            const nonInteger = integerOnly && !Number.isInteger(effective);
            const numericallyInvalid = !Number.isFinite(effective) || effective < 0;
            const showError = !disabled && (duplicate || numericallyInvalid || nonInteger);

            const errorText = showError
              ? resolveErrorText({ invalid: numericallyInvalid, duplicate })
              : undefined;

            return (
              <OptionRow key={value} disabled={disabled} onRemove={handleRemove(value)}>
                <NumberInput
                  value={effective}
                  onChange={(nextValue: number) =>
                    setDrafts((prev) => ({ ...prev, [key]: nextValue }))
                  }
                  minValue={0}
                  decimals={2}
                  unit={unit}
                  disabled={disabled}
                  sx={{ width: '100%' }}
                  error={errorText}
                  onFocus={onInputFocus}
                  onBlur={handleBlur(value)}
                  errorType='floating'
                />
              </OptionRow>
            );
          })}

          {options.length === 0 && (
            <Typography variant='caption' sx={{ opacity: 0.7 }}>
              {t('select_options_editor.no_options', 'No options')}
            </Typography>
          )}
        </Stack>
        <Divider />
        <AddRow
          disabled={disabled}
          value={newText}
          onChange={setNewText}
          placeholder={addPlaceholder}
          canAdd={canAdd}
          showDuplicateError={newTextIsDuplicate}
          invalidErrorText={newTextInvalidErrorText}
          onAdd={addOption}
          onInputFocus={onInputFocus}
          onInputBlur={onInputBlur}
        />
      </Stack>
    </OptionsEditorPopoverShell>
  );
}

export const NumberOptionsEditorPopover = memo(NumberOptionsEditorPopoverImpl);
